import { coerceArray } from '@angular/cdk/coercion';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Inject,
  Injectable,
  InjectionToken,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { of, Observable, forkJoin } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

export const STATIC_FEATURE_FLAGS = new InjectionToken<FeatureFlags>(
  'featureFlags.static'
);

export interface FeatureFlags {
  [flag: string]: boolean;
}

export interface Flags {
  active: boolean;
  key: string;
}

const API = '/api/v1/featureFlags';
const AURA_FF_API = '/api/v4/aura-feature-flags';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  public flags: FeatureFlags = {};
  private cookieService = inject(CookieService);
  private httpClient = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  constructor(
    @Inject(STATIC_FEATURE_FLAGS) private staticFeatureFlags: FeatureFlags
  ) {
    // Sets up a default set for FF checks early in the lifecycle
    this.flags = this.getStaticFFs();
  }

  public load(): Observable<FeatureFlags> {
    return forkJoin({
      cmsFlags: this.getFFsFromCMS().pipe(catchError(() => of({}))),
      auraFlags: this.getAuraFFs().pipe(catchError(() => of({}))),
    }).pipe(
      take(1),
      map(({ cmsFlags, auraFlags }) =>
        this.mergeFlags(
          cmsFlags,
          this.getStaticFFs(),
          this.getFFsFromCookies(),
          auraFlags
        )
      ),
      tap((flags) => {
        // Store global flags, like translations service does. We can read these in v1
        // so as not to make a duplicate request to the v1 ff api
        if (isPlatformBrowser(this.platformId)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any)['featureFlagsV1'] = flags;
        }
      })
    );
  }

  public getFFsFromCMS(): Observable<FeatureFlags> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<FeatureFlags>(API).pipe(
        catchError((e) => {
          console?.warn(
            'rs-shared -> feature-flag.service.ts | Got error when loading feature flags',
            { error: e }
          );

          // Always fallback to static flags for graceful degredation
          return of(this.getStaticFFs() as FeatureFlags);
        }),
        map((data) => data['data'] as unknown as FeatureFlags)
      ) as Observable<FeatureFlags>;
    } else {
      return of({});
    }
  }

  private getAuraFFs(): Observable<FeatureFlags> {
    return this.httpClient
      .get<{ [key: string]: { enabled: boolean } }>(AURA_FF_API)
      .pipe(
        map((response) => {
          const auraFlags: FeatureFlags = {};
          Object.keys(response).forEach((key) => {
            auraFlags[key] = response[key]?.enabled || false;
          });
          return auraFlags;
        }),
        catchError((error) => {
          console.error('Error fetching Aura feature flags', { error });
          return of({});
        })
      );
  }

  public getStaticFFs(): FeatureFlags {
    return this.staticFeatureFlags;
  }

  public getFFsFromCookies(): FeatureFlags {
    const allCookie = this.cookieService.getAll();
    const pickByObject: FeatureFlags = {};

    // Pick only those with quantity > 0
    Object.entries(allCookie).forEach(([k, v]) => {
      if (this.whenFlagCookie(v, k)) {
        pickByObject[k] = v === 'true';
      }
    });
    const mapedKeysObj: FeatureFlags = {};

    Object.entries(pickByObject).forEach(([key, val]) => {
      mapedKeysObj[key.replace(/^ff-/i, '')] = val;
    });

    return mapedKeysObj;
  }

  public hasFlags(flags: string | string[]): boolean {
    return coerceArray(flags).every((current) => this.flags[current]);
  }

  public getValue(key: string): string | number | boolean | undefined {
    return this.flags[key];
  }

  private mergeFlags(
    cmsFlags: FeatureFlags,
    staticFlags: FeatureFlags,
    cookieFlags: FeatureFlags,
    auraFlags: FeatureFlags
  ): FeatureFlags {
    const mergedFlags: FeatureFlags = {
      ...staticFlags,
      ...(cmsFlags || {}),
      ...(auraFlags || {}),
      ...(cookieFlags || {}),
    };

    this.flags = Object.entries(mergedFlags).reduce((acc, [flag, val]) => {
      acc[flag] = val;
      return acc;
    }, {} as FeatureFlags);

    return this.flags;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private whenFlagCookie(v: any, k: string): object {
    return /^ff-/i.test(k) && v;
  }

  private toFlagObject([k, v]: [string, string]): Flags {
    return {
      active: !!(v && v !== 'false'),
      key: k.toLowerCase(),
    };
  }
}
